/**
 * RepConnect - Location Service
 *
 * Manages practice locations and customer-location relationships
 * Part of Phase 2: Multi-Physician Office Lunch Support
 */

import { supabase } from '../utils/supabase';
import { logger } from '../utils/logger';

/**
 * Practice Location Service
 * Manages physical locations where physicians work (offices, hospitals, clinics)
 */
export const locationService = {
  /**
   * Get all practice locations
   * @returns {Promise<Array>} Array of practice locations
   */
  getAllLocations: async () => {
    try {
      const { data, error } = await supabase
        .from('practice_locations')
        .select('*')
        .order('location_name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting all locations:', error);
      throw error;
    }
  },

  /**
   * Get a single practice location by ID
   * @param {string} locationId - UUID of the location
   * @returns {Promise<Object|null>} Location object or null
   */
  getLocationById: async (locationId) => {
    try {
      const { data, error } = await supabase
        .from('practice_locations')
        .select('*')
        .eq('id', locationId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error getting location by ID:', error);
      throw error;
    }
  },

  /**
   * Search locations by name, city, or state
   * Supports autocomplete/typeahead functionality
   * @param {string} searchTerm - Search query
   * @returns {Promise<Array>} Matching locations
   */
  searchLocations: async (searchTerm) => {
    try {
      if (!searchTerm || searchTerm.trim().length === 0) {
        return [];
      }

      const term = searchTerm.trim().toLowerCase();

      // Use full-text search for better performance
      const { data, error } = await supabase
        .from('practice_locations')
        .select('*')
        .or(`location_name.ilike.%${term}%,city.ilike.%${term}%,state.ilike.%${term}%,address.ilike.%${term}%`)
        .order('location_name', { ascending: true })
        .limit(20);

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error searching locations:', error);
      throw error;
    }
  },

  /**
   * Search locations by city and state
   * @param {string} city - City name
   * @param {string} state - State abbreviation (e.g., 'CA')
   * @returns {Promise<Array>} Matching locations
   */
  searchByLocation: async (city, state) => {
    try {
      let query = supabase
        .from('practice_locations')
        .select('*');

      if (city) {
        query = query.ilike('city', `%${city}%`);
      }

      if (state) {
        query = query.eq('state', state);
      }

      const { data, error } = await query
        .order('location_name', { ascending: true })
        .limit(50);

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error searching locations by city/state:', error);
      throw error;
    }
  },

  /**
   * Create a new practice location
   * @param {Object} locationData - Location details
   * @param {string} locationData.location_name - Name of the location
   * @param {string} locationData.address - Street address
   * @param {string} locationData.city - City
   * @param {string} locationData.state - State abbreviation
   * @param {string} locationData.zip - ZIP code
   * @param {string} locationData.phone - Phone number
   * @param {string} locationData.location_type - Type: 'office', 'hospital_department', 'clinic', 'institution'
   * @returns {Promise<Object>} Created location
   */
  createLocation: async (locationData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('practice_locations')
        .insert([{
          ...locationData,
          created_by: user.id,
          is_verified: false
        }])
        .select()
        .single();

      if (error) throw error;

      logger.info('Location created:', { locationId: data.id, name: data.location_name });
      return data;
    } catch (error) {
      logger.error('Error creating location:', error);
      throw error;
    }
  },

  /**
   * Update an existing practice location
   * Uses RPC function to bypass RLS (allows customers/office_staff to edit locations)
   * @param {string} locationId - UUID of the location
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated location
   */
  updateLocation: async (locationId, updates) => {
    try {
      // Use RPC function to bypass RLS
      const { data, error } = await supabase.rpc('update_practice_location', {
        location_id_param: locationId,
        location_name_param: updates.location_name || null,
        address_param: updates.address || null,
        city_param: updates.city || null,
        state_param: updates.state || null,
        zip_param: updates.zip || null,
        phone_param: updates.phone || null
      });

      if (error) {
        // Fallback to direct update if RPC doesn't exist
        logger.warn('RPC update_practice_location failed, trying direct update:', error);
        const { data: directData, error: directError } = await supabase
          .from('practice_locations')
          .update(updates)
          .eq('id', locationId)
          .select()
          .single();

        if (directError) throw directError;
        logger.info('Location updated (direct):', { locationId });
        return directData;
      }

      logger.info('Location updated:', { locationId });
      return data;
    } catch (error) {
      logger.error('Error updating location:', error);
      throw error;
    }
  }
};

/**
 * Customer Location Service
 * Manages physician-location relationships and availability
 */
export const customerLocationService = {
  /**
   * Find office staff who is delegating for a given physician
   * Used to get locations for placeholder physicians created during office staff onboarding
   * @param {string} physicianId - UUID of the physician (placeholder or registered)
   * @returns {Promise<Object|null>} The delegating user's profile or null
   */
  getDelegatingUserForPhysician: async (physicianId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, user_type')
        .eq('delegating_for', physicianId)
        .eq('user_type', 'office_staff')
        .maybeSingle();

      if (error) {
        throw error;
      }

      return data; // Returns null if no rows found
    } catch (error) {
      logger.error('Error finding delegating user:', error);
      return null;
    }
  },

  /**
   * Get all locations for a specific customer (physician)
   * Falls back to checking for locations configured by office staff who delegates for this physician
   * @param {string} customerId - UUID of the customer
   * @returns {Promise<Array>} Array of customer locations with practice details
   */
  getCustomerLocations: async (customerId) => {
    try {
      // First, try to get locations directly for this customer
      const { data, error } = await supabase
        .from('customer_locations')
        .select(`
          *,
          practice_locations (
            id,
            location_name,
            address,
            city,
            state,
            zip,
            phone,
            location_type
          )
        `)
        .eq('customer_id', customerId)
        .order('is_primary_location', { ascending: false });

      if (error) throw error;

      // If locations found, return them
      if (data && data.length > 0) {
        return data;
      }

      // No locations found for this customer - check if there's office staff delegating for them
      // This handles placeholder physicians created during office staff onboarding
      logger.debug('No locations found for customer, checking for delegating user', { customerId });

      const delegatingUser = await customerLocationService.getDelegatingUserForPhysician(customerId);

      if (delegatingUser) {
        logger.debug('Found delegating user, fetching their locations', {
          physicianId: customerId,
          delegatingUserId: delegatingUser.id,
          delegatingUserName: `${delegatingUser.first_name} ${delegatingUser.last_name}`
        });

        // Get locations configured by the delegating office staff
        const { data: staffLocations, error: staffError } = await supabase
          .from('customer_locations')
          .select(`
            *,
            practice_locations (
              id,
              location_name,
              address,
              city,
              state,
              zip,
              phone,
              location_type
            )
          `)
          .eq('customer_id', delegatingUser.id)
          .order('is_primary_location', { ascending: false });

        if (staffError) throw staffError;

        if (staffLocations && staffLocations.length > 0) {
          logger.info('Using locations from delegating office staff', {
            physicianId: customerId,
            staffId: delegatingUser.id,
            locationCount: staffLocations.length
          });
          return staffLocations;
        }
      }

      return [];
    } catch (error) {
      logger.error('Error getting customer locations:', error);
      throw error;
    }
  },

  /**
   * Get physicians at a specific location
   * Filters out office staff - only returns physicians/providers
   * @param {string} locationId - UUID of the practice location
   * @returns {Promise<Array>} Array of physicians at this location
   */
  getPhysiciansAtLocation: async (locationId) => {
    try {
      const { data, error } = await supabase
        .from('customer_locations')
        .select(`
          *,
          user_profiles:customer_id (
            id,
            first_name,
            last_name,
            email,
            specialty,
            user_type
          )
        `)
        .eq('location_id', locationId)
        .order('is_primary_location', { ascending: false });

      if (error) throw error;

      // Filter to only include physicians/providers, not office staff
      const physicians = (data || []).filter(record => {
        const userType = record.user_profiles?.user_type?.toLowerCase();
        return userType === 'physician' || userType === 'provider' || userType === 'customer';
      });

      return physicians;
    } catch (error) {
      logger.error('Error getting physicians at location:', error);
      throw error;
    }
  },

  /**
   * Get physicians available at a location on a specific day of week
   * Filters out office staff - only returns physicians/providers
   * @param {string} locationId - UUID of the practice location
   * @param {string} dayOfWeek - Day name: 'monday', 'tuesday', etc.
   * @returns {Promise<Array>} Array of available physicians
   */
  getPhysiciansAvailableOnDay: async (locationId, dayOfWeek) => {
    try {
      const dayColumn = `works_${dayOfWeek.toLowerCase()}`;

      const { data, error} = await supabase
        .from('customer_locations')
        .select(`
          *,
          user_profiles:customer_id (
            id,
            first_name,
            last_name,
            email,
            specialty,
            user_type
          )
        `)
        .eq('location_id', locationId)
        .eq(dayColumn, true);

      if (error) throw error;

      // Filter to only include physicians/providers, not office staff
      const physicians = (data || []).filter(record => {
        const userType = record.user_profiles?.user_type?.toLowerCase();
        return userType === 'physician' || userType === 'provider' || userType === 'customer';
      });

      return physicians;
    } catch (error) {
      logger.error('Error getting physicians available on day:', error);
      throw error;
    }
  },

  /**
   * Link a customer to a practice location with availability
   * @param {Object} linkData - Customer-location relationship data
   * @param {string} linkData.customer_id - UUID of the physician
   * @param {string} linkData.location_id - UUID of the practice location
   * @param {boolean} linkData.is_primary_location - Is this their primary location?
   * @param {boolean} linkData.works_monday - Available on Mondays
   * @param {boolean} linkData.works_tuesday - Available on Tuesdays
   * @param {boolean} linkData.works_wednesday - Available on Wednesdays
   * @param {boolean} linkData.works_thursday - Available on Thursdays
   * @param {boolean} linkData.works_friday - Available on Fridays
   * @param {boolean} linkData.works_saturday - Available on Saturdays
   * @param {boolean} linkData.works_sunday - Available on Sundays
   * @param {string} linkData.role_at_location - Role/title at this location
   * @param {string} linkData.department - Department name
   * @returns {Promise<Object>} Created customer-location link
   */
  linkCustomerToLocation: async (linkData) => {
    try {
      const { data, error } = await supabase
        .from('customer_locations')
        .insert([linkData])
        .select()
        .single();

      if (error) {
        // Handle unique constraint violation
        if (error.code === '23505') {
          throw new Error('Customer is already linked to this location');
        }
        throw error;
      }

      logger.info('Customer linked to location:', {
        customerId: linkData.customer_id,
        locationId: linkData.location_id
      });

      return data;
    } catch (error) {
      logger.error('Error linking customer to location:', error);
      throw error;
    }
  },

  /**
   * Update customer-location relationship (e.g., change availability)
   * Uses RPC function to bypass RLS
   * @param {string} customerLocationId - UUID of the customer_locations record
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated customer-location record
   */
  updateCustomerLocation: async (customerLocationId, updates) => {
    try {
      // Use RPC function to bypass RLS
      const { data, error } = await supabase.rpc('update_customer_location', {
        customer_location_id_param: customerLocationId,
        works_monday_param: updates.works_monday ?? null,
        works_tuesday_param: updates.works_tuesday ?? null,
        works_wednesday_param: updates.works_wednesday ?? null,
        works_thursday_param: updates.works_thursday ?? null,
        works_friday_param: updates.works_friday ?? null,
        works_saturday_param: updates.works_saturday ?? null,
        works_sunday_param: updates.works_sunday ?? null,
        is_primary_location_param: updates.is_primary_location ?? null,
        role_at_location_param: updates.role_at_location || null,
        department_param: updates.department || null
      });

      if (error) {
        // Fallback to direct update if RPC doesn't exist
        logger.warn('RPC update_customer_location failed, trying direct update:', error);
        const { data: directData, error: directError } = await supabase
          .from('customer_locations')
          .update(updates)
          .eq('id', customerLocationId)
          .select()
          .single();

        if (directError) throw directError;
        logger.info('Customer location updated (direct):', { customerLocationId });
        return directData;
      }

      logger.info('Customer location updated:', { customerLocationId });
      return data;
    } catch (error) {
      logger.error('Error updating customer location:', error);
      throw error;
    }
  },

  /**
   * Remove customer-location link
   * Also cleans up the associated provider_availability record for that location
   * @param {string} customerLocationId - UUID of the customer_locations record
   * @returns {Promise<void>}
   */
  unlinkCustomerFromLocation: async (customerLocationId) => {
    try {
      // First, get the customer_id and location_id before deleting
      const { data: linkData, error: fetchError } = await supabase
        .from('customer_locations')
        .select('customer_id, location_id')
        .eq('id', customerLocationId)
        .single();

      if (fetchError) {
        logger.error('Error fetching customer_location record:', fetchError);
        throw fetchError;
      }

      const { customer_id, location_id } = linkData;

      // Delete the customer_locations record
      const { error } = await supabase
        .from('customer_locations')
        .delete()
        .eq('id', customerLocationId);

      if (error) throw error;

      logger.info('Customer unlinked from location:', { customerLocationId, customer_id, location_id });

      // Also clean up the provider_availability record for this location
      // This prevents orphaned blocked periods from showing up
      if (location_id) {
        const { error: availError } = await supabase
          .from('provider_availability')
          .delete()
          .eq('provider_id', customer_id)
          .eq('location_id', location_id);

        if (availError) {
          logger.warn('Error deleting provider_availability for location (may not exist):', availError);
          // Don't throw - availability record may not exist, which is fine
        } else {
          logger.info('Cleaned up provider_availability for removed location:', { customer_id, location_id });
        }
      }
    } catch (error) {
      logger.error('Error unlinking customer from location:', error);
      throw error;
    }
  },

  /**
   * Get primary location for a customer
   * Falls back to checking for primary location configured by office staff who delegates for this physician
   * @param {string} customerId - UUID of the customer
   * @returns {Promise<Object|null>} Primary location or null
   */
  getPrimaryLocation: async (customerId) => {
    try {
      const { data, error } = await supabase
        .from('customer_locations')
        .select(`
          *,
          practice_locations (
            id,
            location_name,
            address,
            city,
            state,
            zip,
            phone,
            location_type
          )
        `)
        .eq('customer_id', customerId)
        .eq('is_primary_location', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No primary location set - check for delegating user
          logger.debug('No primary location for customer, checking delegating user', { customerId });

          const delegatingUser = await customerLocationService.getDelegatingUserForPhysician(customerId);

          if (delegatingUser) {
            const { data: staffPrimaryLocation, error: staffError } = await supabase
              .from('customer_locations')
              .select(`
                *,
                practice_locations (
                  id,
                  location_name,
                  address,
                  city,
                  state,
                  zip,
                  phone,
                  location_type
                )
              `)
              .eq('customer_id', delegatingUser.id)
              .eq('is_primary_location', true)
              .single();

            if (!staffError && staffPrimaryLocation) {
              logger.info('Using primary location from delegating office staff', {
                physicianId: customerId,
                staffId: delegatingUser.id
              });
              return staffPrimaryLocation;
            }
          }

          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error getting primary location:', error);
      throw error;
    }
  }
};
